import { Stack, SummaryMessage, Text } from "@certn/cascadia-react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

export type PlaceOrderDisabledReason =
  | "NO_RECIPIENTS"
  | "MISSING_CARD_ON_FILE"
  | "MISSING_PAYMENT_METHOD"
  | "MISSING_BILLING_ADDRESS"
  | "ORDER_BLOCKED"
  | "CERTIFICATION_NOT_ACKNOWLEDGED"
  | "CV_REVIEW_INVALID";

interface PlaceOrderDisabledConditions {
  hasNoRecipients?: boolean;
  isMissingCardOnFile?: boolean;
  isMissingPaymentMethod?: boolean;
  isMissingBillingAddress?: boolean;
  isOrderBlocked?: boolean;
  isCertificationUnacknowledged?: boolean;
  isCvReviewInvalid?: boolean;
}

export function getPlaceOrderDisabledReasons(
  conditions: PlaceOrderDisabledConditions
): PlaceOrderDisabledReason[] {
  const reasons: [boolean | undefined, PlaceOrderDisabledReason][] = [
    [conditions.hasNoRecipients, "NO_RECIPIENTS"],
    [conditions.isMissingCardOnFile, "MISSING_CARD_ON_FILE"],
    [conditions.isMissingPaymentMethod, "MISSING_PAYMENT_METHOD"],
    [conditions.isMissingBillingAddress, "MISSING_BILLING_ADDRESS"],
    [conditions.isOrderBlocked, "ORDER_BLOCKED"],
    [
      conditions.isCertificationUnacknowledged,
      "CERTIFICATION_NOT_ACKNOWLEDGED",
    ],
    [conditions.isCvReviewInvalid, "CV_REVIEW_INVALID"],
  ];
  return reasons.filter(([applies]) => applies).map(([, reason]) => reason);
}

function getReasonMessage(
  reason: PlaceOrderDisabledReason,
  t: TFunction
): string | null {
  switch (reason) {
    case "NO_RECIPIENTS":
      return t("Add at least one recipient in the Order details step.");
    case "MISSING_CARD_ON_FILE":
      return t("Add a credit card to your billing profile to place orders.");
    case "MISSING_PAYMENT_METHOD":
      return t("Add a payment method below.");
    case "MISSING_BILLING_ADDRESS":
      return t("Add your billing address below.");
    case "CERTIFICATION_NOT_ACKNOWLEDGED":
      return t("Confirm the consumer report certification below.");
    case "CV_REVIEW_INVALID":
      return t("Fix the CV Review details below.");
    case "ORDER_BLOCKED":
      // Explained in detail by OrderBlockedMessage.
      return null;
  }
}

interface Props {
  reasons: PlaceOrderDisabledReason[];
}

export function PlaceOrderDisabledReasons({ reasons }: Props) {
  const { t } = useTranslation();
  const messages = reasons
    .map((reason) => getReasonMessage(reason, t))
    .filter((message): message is string => message !== null);

  if (messages.length === 0) {
    return null;
  }

  return (
    <SummaryMessage type="warning">
      <Stack gap="space-2">
        <Text color="text-base" style="global/heading-small">
          {t("You can't send this order yet")}
        </Text>
        <ul>
          {messages.map((message) => (
            <li key={message}>
              <Text color="text-weak" style="global/body-secondary">
                {message}
              </Text>
            </li>
          ))}
        </ul>
      </Stack>
    </SummaryMessage>
  );
}
